import React, { useEffect, useState } from 'react';
import { useLeads } from '../../services/leads';
import { effectiveStage, highestQuote, soldValue, useFollowUps } from '../../services/followUps';
import { useDrafts } from '../../services/drafts';
import { listConversations } from '../../services/communications';
import { syncSummary, useSync } from '../../services/sync';
import { CURRENT_USER } from '../../services/currentUser';
import './Dashboard.css';

/**
 * Dashboard — today's to-dos, today's numbers, and this month so far.
 * Everything is read from the same stores the other pages use, so it's live.
 * Money (sold, pipeline) is admin-only. "Sold" = jobs booked × the package that sold;
 * collected payments come later from Zoho Books.
 */

const DAY = 86400000;
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const inRange = (iso, from, to) => {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return t >= from.getTime() && t < to.getTime();
};

const money = (n) => `$${Math.round(n).toLocaleString('en-CA')}`;

// "▲ $1,240 vs last month" — this month so far vs the same days of last month
const Delta = ({ now, before, format = (n) => n }) => {
  const diff = now - before;
  if (!diff) return <span className="dash-delta flat">Same as this point last month</span>;
  return (
    <span className={`dash-delta ${diff > 0 ? 'up' : 'down'}`}>
      {diff > 0 ? '▲' : '▼'} {format(Math.abs(diff))} vs this point last month
    </span>
  );
};

const Todo = ({ icon, label, count, tone = '', onClick, children }) => (
  <button type="button" className={`dash-todo ${tone}${count ? '' : ' done'}`} onClick={onClick}>
    <span className="dash-todo-icon" aria-hidden="true">{icon}</span>
    <span className="dash-todo-main">
      <span className="dash-todo-label">{label}</span>
      {children}
    </span>
    <span className="dash-todo-count">{count || '✓'}</span>
    <span className="dash-todo-go" aria-hidden="true">›</span>
  </button>
);

const Dashboard = ({ onNavigate }) => {
  const leads = useLeads();
  const followUps = useFollowUps();
  const drafts = useDrafts();
  const sync = useSync();
  const [unread, setUnread] = useState(null);

  useEffect(() => {
    let live = true;
    listConversations()
      .then((list) => live && setUnread(list.filter((c) => c.unreadCount > 0).length))
      .catch(() => live && setUnread(null));
    return () => {
      live = false;
    };
  }, []);

  const now = new Date();
  const today = startOfDay(now);
  const tomorrow = new Date(today.getTime() + DAY);
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  // the same stretch of last month: 1st → this day/time (clamped to a shorter month)
  const lastStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastEnd = new Date(lastStart.getTime() + (now.getTime() - monthStart.getTime()));
  const lastEndClamped = lastEnd > monthStart ? monthStart : lastEnd;

  /* ---------- to-dos ---------- */
  const withStage = followUps.map((f) => ({ ...f, shown: effectiveStage(f) }));
  const requests = withStage.filter((f) => f.bookingRequest && f.shown === 'call_now');
  const callNow = withStage.filter((f) => f.shown === 'call_now' && !f.bookingRequest);
  const rebook = leads.filter((l) => l.stage === 'Rebook');
  const { state: syncState, count: syncCount } = syncSummary(sync);
  const syncFailed = syncState === 'failed' ? syncCount : 0;

  /* ---------- today ---------- */
  const todaysVisits = leads.filter(
    (l) => inRange(l.appointmentStart, today, tomorrow) && ['Inspection Booked', 'Report Pending', 'Report Sent'].includes(l.stage)
  );
  const visitsDone = todaysVisits.filter((l) => l.stage === 'Report Pending' || l.stage === 'Report Sent').length;
  const reportsToday = followUps.filter((f) => inRange(f.quotedAt, today, tomorrow)).length;
  const bookedToday = followUps.filter((f) => inRange(f.bookedAt, today, tomorrow));

  /* ---------- month so far ---------- */
  const bookedIn = (from, to) => followUps.filter((f) => f.booked && inRange(f.bookedAt, from, to));
  const reportsIn = (from, to) => followUps.filter((f) => inRange(f.quotedAt, from, to)).length;
  const sum = (list) => list.reduce((s, f) => s + soldValue(f), 0);
  const month = { booked: bookedIn(monthStart, tomorrow), reports: reportsIn(monthStart, tomorrow) };
  const last = { booked: bookedIn(lastStart, lastEndClamped), reports: reportsIn(lastStart, lastEndClamped) };
  const closeRate = month.reports ? Math.round((month.booked.length / month.reports) * 100) : null;
  const open = withStage.filter((f) => f.shown === 'scheduled' || f.shown === 'call_now');
  const pipeline = open.reduce((s, f) => s + highestQuote(f), 0);

  const monthName = now.toLocaleDateString('en-US', { month: 'long' });

  return (
    <div className="dash">
      <div className="dash-head">
        <h1>Dashboard</h1>
        <p>{now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
      </div>

      <div className="dash-scroll">
        {/* ---------- to-do ---------- */}
        <section className="dash-section">
          <h2>To-do today</h2>
          <div className="dash-todos">
            <Todo icon="🔥" label="Booking requests from reports" count={requests.length} tone="hot" onClick={() => onNavigate('calls', { tab: 'call_now' })}>
              {requests.slice(0, 3).map((f) => (
                <span key={f.id} className="dash-todo-sub">
                  {f.name}
                  {f.bookingRequest.quoteName ? ` · ${f.bookingRequest.quoteName}` : ''}
                </span>
              ))}
            </Todo>
            <Todo icon="📞" label="Call Now follow-ups" count={callNow.length} onClick={() => onNavigate('calls', { tab: 'call_now' })} />
            <Todo icon="📝" label="Unfinished inspections" count={drafts.length} onClick={() => onNavigate('inspection', { tab: 'previous' })} />
            <Todo icon="↻" label="Leads to rebook" count={rebook.length} onClick={() => onNavigate('rebook')} />
            <Todo icon="💬" label="Unread messages" count={unread || 0} onClick={() => onNavigate('communications')} />
            {syncFailed > 0 && (
              <Todo icon="⚠️" label="Changes that didn’t reach Zoho" count={syncFailed} tone="warn" onClick={() => onNavigate('sync')} />
            )}
          </div>
        </section>

        {/* ---------- today ---------- */}
        <section className="dash-section">
          <h2>Today</h2>
          <div className="dash-tiles">
            <button type="button" className="dash-tile" onClick={() => onNavigate('route')}>
              <span className="dash-tile-label">Inspections</span>
              <span className="dash-tile-value">
                {visitsDone}<small>/{todaysVisits.length}</small>
              </span>
              <span className="dash-tile-sub">
                {todaysVisits.length ? `${todaysVisits.length - visitsDone} left` : 'None booked'}
              </span>
            </button>
            <div className="dash-tile">
              <span className="dash-tile-label">Reports sent</span>
              <span className="dash-tile-value">{reportsToday}</span>
            </div>
            <button type="button" className="dash-tile" onClick={() => onNavigate('calls', { tab: 'call_now' })}>
              <span className="dash-tile-label">Calls due</span>
              <span className="dash-tile-value">{requests.length + callNow.length}</span>
            </button>
            <button type="button" className="dash-tile" onClick={() => onNavigate('calls', { tab: 'booked' })}>
              <span className="dash-tile-label">Jobs booked</span>
              <span className="dash-tile-value">{bookedToday.length}</span>
              {CURRENT_USER.admin && bookedToday.length > 0 && <span className="dash-tile-sub">{money(sum(bookedToday))} sold</span>}
            </button>
          </div>
        </section>

        {/* ---------- month so far ---------- */}
        <section className="dash-section">
          <h2>{monthName} so far</h2>

          {CURRENT_USER.admin && (
            <div className="dash-income">
              <span className="dash-income-label">Sold this month</span>
              <span className="dash-income-value">{money(sum(month.booked))}</span>
              <Delta now={sum(month.booked)} before={sum(last.booked)} format={money} />
              <span className="dash-income-note">Value of jobs booked — collected payments come later.</span>
            </div>
          )}

          <div className="dash-tiles">
            <button type="button" className="dash-tile" onClick={() => onNavigate('calls', { tab: 'booked' })}>
              <span className="dash-tile-label">Jobs booked</span>
              <span className="dash-tile-value">{month.booked.length}</span>
              <Delta now={month.booked.length} before={last.booked.length} />
            </button>
            <div className="dash-tile">
              <span className="dash-tile-label">Reports sent</span>
              <span className="dash-tile-value">{month.reports}</span>
              <Delta now={month.reports} before={last.reports} />
            </div>
            <div className="dash-tile">
              <span className="dash-tile-label">Close rate</span>
              <span className="dash-tile-value">{closeRate == null ? '—' : `${closeRate}%`}</span>
              <span className="dash-tile-sub">Booked ÷ reports sent</span>
            </div>
            {CURRENT_USER.admin && (
              <button type="button" className="dash-tile" onClick={() => onNavigate('calls', { tab: 'scheduled' })}>
                <span className="dash-tile-label">Open pipeline</span>
                <span className="dash-tile-value">{money(pipeline)}</span>
                <span className="dash-tile-sub">
                  {open.length} quote{open.length === 1 ? '' : 's'} in follow-up
                </span>
              </button>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Dashboard;
